import React, { useState, useEffect } from "react";
import SpeAccDataService from "../services/SpeAccService";
import UserDataService from "../services/UserService";
import StampDutyService from "../services/StampDutyService";
import { formatWithCommas, formatToTwoDecimals } from "../../utils/formatNumber";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  InputAdornment
} from "@mui/material";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AddSpeAcc = () => {
  const { owner } = useParams();
  const navigate = useNavigate();
  const initialSpeAccState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "credit",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null
  };

  const [SpeAcc, setSpeAcc] = useState(initialSpeAccState);
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState("50");
  const [waiveReason, setWaiveReason] = useState("");

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "amount") {
      setSpeAcc(prev => ({ ...prev, amount: formatWithCommas(value) }));
    } else {
      setSpeAcc(prev => ({ ...prev, [name]: value }));
    }
  };

  const saveSpeAcc = async () => {
    setError("");
    const cleanAmount = parseFloat(String(SpeAcc.amount).replace(/,/g, ""));
    if (!SpeAcc.amount || Number.isNaN(cleanAmount) || cleanAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (!SpeAcc.creditOrDebit) {
      setError("Please select transaction type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        owner: SpeAcc.owner,
        date: typeof SpeAcc.date === "string" ? SpeAcc.date : toDateInput(SpeAcc.date),
        amount: cleanAmount,
        creditOrDebit: String(SpeAcc.creditOrDebit),
        description: (SpeAcc.description || "").trim()
      };

      const res = await SpeAccDataService.create(SpeAcc.owner, payload);
      const r = res?.data || {};
      const ownerId = (r.owner && typeof r.owner === "object") ? (r.owner._id || r.owner.id || String(r.owner)) : (r.owner ?? SpeAcc.owner);

      // Record Stamp Duty or waiver for credit deposit
      if (SpeAcc.creditOrDebit === "credit") {
        const dutyNum = applyStampDuty ? (parseFloat(String(stampDutyAmount).replace(/,/g, "")) || 50) : 0;
        StampDutyService.record({
          memberId: ownerId,
          depositAmount: payload.amount,
          stampDutyAmount: dutyNum,
          accountType: "speacc",
          isWaived: !applyStampDuty,
          waiveReason: !applyStampDuty ? (waiveReason || "Waived on Special Account deposit") : "",
          date: payload.date,
          notes: payload.description || `Special Account deposit (Stamp Duty: ₦${dutyNum})`
        }).catch(err => console.error("Stamp duty recording error:", err));
      }

      setSpeAcc({
        owner: ownerId,
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null
      });

      navigate(`/dashboard/SpeAcc/${encodeURIComponent(ownerId)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newSpeAcc = () => {
    setSpeAcc({ ...initialSpeAccState, owner: owner || initialSpeAccState.owner });
    setSubmitted(false);
    setError("");
    if (!owner) setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add SpeAcc Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newSpeAcc}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={SpeAcc.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                value={SpeAcc.amount}
                onChange={handleInputChange}
                onBlur={() => {
                  if (SpeAcc.amount) {
                    setSpeAcc(prev => ({ ...prev, amount: formatToTwoDecimals(prev.amount) }));
                  }
                }}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  inputMode: "numeric"
                }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Transaction Type</Typography>
                <Button
                  variant={SpeAcc.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setSpeAcc(s => ({ ...s, creditOrDebit: "credit" }))}
                >
                  Credit
                </Button>
                <Button
                  variant={SpeAcc.creditOrDebit === "debit" ? "contained" : "outlined"}
                  onClick={() => setSpeAcc(s => ({ ...s, creditOrDebit: "debit" }))}
                >
                  Debit
                </Button>
              </Stack>
            </Grid>

            {/* STAMP DUTY CONTROL FOR DEPOSIT */}
            {SpeAcc.creditOrDebit === "credit" && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0"
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                        Stamp Duty (EMTL)
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={applyStampDuty}
                          onChange={(e) => setApplyStampDuty(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={applyStampDuty ? `Apply ₦${stampDutyAmount || 50} Stamp Duty` : "Waive Stamp Duty"}
                      sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                    />
                  </Box>

                  {applyStampDuty ? (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                        Duty Amount:
                      </Typography>
                      {[50, 100, 200].map((preset) => (
                        <Button
                          key={preset}
                          size="small"
                          variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                          onClick={() => setStampDutyAmount(String(preset))}
                          sx={{
                            minWidth: 64,
                            py: 0.3,
                            px: 1,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                            color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                            borderColor: "#0D9488"
                          }}
                        >
                          ₦{preset} {preset === 50 ? "(Std)" : ""}
                        </Button>
                      ))}
                      <TextField
                        size="small"
                        type="number"
                        label="Custom Duty (₦)"
                        value={stampDutyAmount}
                        onChange={(e) => setStampDutyAmount(e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 140, backgroundColor: "#ffffff" }}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Reason for waiving (e.g. Single bulk transfer split across savings/shares/loan/special account)"
                      value={waiveReason}
                      onChange={(e) => setWaiveReason(e.target.value)}
                      sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                    />
                  )}
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={SpeAcc.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveSpeAcc}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newSpeAcc} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => window.history.back()}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AddSpeAcc;
